import { Component, ErrorHandler, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { AbstractControl } from '@angular/forms'
import { form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { Observable, Subject, combineLatest } from 'rxjs'
import { map, startWith, takeUntil } from 'rxjs/operators'

import { DateService } from '@lemon/core/service/date.service'

import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { Kirjanpitaja, KirjanpitajanTiimi, KirjanpitajanTiimikokonaisuus, KirjanpitajanTiiminJasen } from 'app/_jaettu-lemonator/model/kirjanpitaja'

import { TiimienHallintaService, TiimiRooli } from './tiimien-hallinta.service'

export interface LisaaJasenDialogData {
  // Lisätäänkö tiimiin vai tiimikokonaisuuteen, ja kumpaan rooliin (vain tiimissä)
  kohde:
    | { tyyppi: 'tiimi'; tiimiAvain: KirjanpitajanTiimi['avain']; tiimiNimi: string; rooli: TiimiRooli }
    | { tyyppi: 'kokonaisuus'; kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain']; kokonaisuusNimi: string }
}

interface LisaaJasenLomake {
  kirjanpitaja: Kirjanpitaja | string | null
  alkupaiva: Date | null
  primary: boolean
}

@Component({
  templateUrl: './lisaa-jasen.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LisaaJasenDialog implements OnInit, OnDestroy {
  lomake = form(signal<LisaaJasenLomake>({ kirjanpitaja: null, alkupaiva: new Date(), primary: false }), polku => {
    required(polku.kirjanpitaja)
    validoiReaktiivisesti(polku.kirjanpitaja, kirjanpitajaValittuValidator)
    required(polku.alkupaiva)
  })

  suodatetutKirjanpitajatObservable: Observable<Kirjanpitaja[]>
  virhe: string = null

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LisaaJasenDialogData,
    private _dialogRef: MatDialogRef<LisaaJasenDialog>,
    private _kirjanpitajaService: KirjanpitajaService,
    private _hallintaService: TiimienHallintaService,
    private _dateService: DateService,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
  ) {}

  ngOnInit() {
    const hakutekstiObservable = arvonMuutokset(this.lomake.kirjanpitaja, { injector: this._injector }).pipe(startWith<Kirjanpitaja | string>(''))
    this.suodatetutKirjanpitajatObservable = combineLatest([this._kirjanpitajaService.kirjanpitajatObservable, hakutekstiObservable]).pipe(
      map(([kaikki, haku]) => {
        const aktiiviset = (kaikki || []).filter(k => !k.poistettu)
        if (!haku) {
          return aktiiviset
        }
        if (typeof haku !== 'string') {
          return aktiiviset
        }
        const lower = haku.toLowerCase()
        return aktiiviset.filter(k => `${k.etunimi} ${k.sukunimi}`.toLowerCase().includes(lower) || (k.email || '').toLowerCase().includes(lower))
      }),
      takeUntil(this._ngUnsubscribe),
    )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  kirjanpitajaDisplay = (kirjanpitaja: Kirjanpitaja | string): string => {
    if (!kirjanpitaja) {
      return ''
    }
    if (typeof kirjanpitaja === 'string') {
      return kirjanpitaja
    }
    return `${kirjanpitaja.etunimi} ${kirjanpitaja.sukunimi}`
  }

  peruuta() {
    this._dialogRef.close()
  }

  async tallenna() {
    if (!this.lomake().valid()) {
      return
    }
    this.virhe = null
    const arvo = this.lomake().value()
    if (!arvo.kirjanpitaja || typeof arvo.kirjanpitaja === 'string') {
      this.virhe = 'Valitse kirjanpitäjä listalta.'
      return
    }
    const alku = this._dateService.dateToNumber(arvo.alkupaiva)
    const jakso: KirjanpitajanTiiminJasen = {
      kirjanpitajaAvain: arvo.kirjanpitaja.avain,
      alku,
      loppu: null,
    }
    if (arvo.primary) {
      jakso.primary = true
    }
    try {
      this._ladataanService.aloitaLataaminen()
      if (this.data.kohde.tyyppi === 'tiimi') {
        await this._hallintaService.lisaaTiiminJasen(this.data.kohde.tiimiAvain, this.data.kohde.rooli, jakso)
      } else {
        await this._hallintaService.lisaaKokonaisuudenEsihenkilo(this.data.kohde.kokonaisuusAvain, jakso)
      }
      this._snackbar.open('Jäsen lisätty', 'OK', { duration: 5000 })
      this._dialogRef.close(true)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Lisääminen epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }
}

function kirjanpitajaValittuValidator(control: AbstractControl<Kirjanpitaja | string>) {
  const arvo = control.value
  if (arvo && typeof arvo !== 'string' && arvo.avain) {
    return null
  }
  return { kirjanpitajaaEiValittu: true }
}
